import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { getUsage } from "@/lib/usage.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { Header } from "@/components/Header";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { useSubscriptionRealtime } from "@/hooks/useSubscriptionRealtime";
import { useT, useDocumentTitleKey } from "@/i18n/locale-context";

export const Route = createFileRoute("/checkout/return")({
  head: () => ({ meta: [{ title: "Paiement confirmé — Glow Up Coach" }] }),
  validateSearch: (s: Record<string, unknown>): { session_id?: string } => ({
    session_id: typeof s.session_id === "string" ? s.session_id : undefined,
  }),
  component: CheckoutReturn,
});

function CheckoutReturn() {
  useDocumentTitleKey("checkout.return.head.title");
  const t = useT();
  const nav = useNavigate();
  const queryClient = useQueryClient();
  const usage = useServerFn(getUsage);
  const env = getStripeEnvironment();
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) nav({ to: "/login" });
      else setUserId(data.session.user.id);
    });
  }, [nav]);

  // Realtime: webhook will UPSERT the subscription row → invalidate usage.
  useSubscriptionRealtime(userId);

  // Poll briefly as a fallback while the webhook is in flight (refetch every
  // 2s, stop as soon as `isPremium` flips true). Realtime usually wins, but
  // polling guarantees we don't get stuck if the channel hasn't subscribed
  // before the webhook fires.
  const { data } = useQuery({
    queryKey: ["usage", env],
    queryFn: () => usage({ data: { environment: env } }),
    enabled: !!userId,
    refetchInterval: (q) => (q.state.data?.isPremium ? false : 2000),
  });

  // Once Premium is active, push the user to billing.
  useEffect(() => {
    if (data?.isPremium) {
      queryClient.invalidateQueries({ queryKey: ["usage"] });
      const timer = setTimeout(() => nav({ to: "/billing" }), 1200);
      return () => clearTimeout(timer);
    }
  }, [data?.isPremium, nav, queryClient]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PaymentTestModeBanner />
      <Header />
      <div className="max-w-md mx-auto px-6 py-20 text-center">
        {data?.isPremium ? (
          <>
            <div className="text-5xl mb-4">✓</div>
            <h1
              className="font-display font-bold text-3xl mb-2 tracking-tight"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {t("checkout.return.success.title")}
            </h1>
            <p className="text-muted-foreground text-sm mb-6">
              {t("checkout.return.success.desc")}
            </p>
            <Link
              to="/billing"
              className="inline-block bg-foreground text-background px-6 py-3 rounded-xl text-sm font-semibold hover:opacity-90"
            >
              {t("checkout.return.success.cta")}
            </Link>
          </>
        ) : (
          <>
            <div className="animate-pulse text-5xl mb-4">…</div>
            <h1
              className="font-display font-bold text-2xl mb-2 tracking-tight"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {t("checkout.return.pending.title")}
            </h1>
            <p className="text-muted-foreground text-sm">
              {t("checkout.return.pending.desc")}
            </p>
          </>
        )}
      </div>
    </div>
  );
}